import React, { useState, useEffect } from "react";
import {
	Container,
	Row,
	Col,
	Button,
	Form,
	Card,
} from "react-bootstrap";
import Modal from "../../../common/components/ThemedModal";
import {
	createRider,
	deleteRider,
	updateRider,
	uploadRiderImage,
} from "../../../../services/riderService";
import { getSeries, getSports } from "../../../../services/series";
import {
	BrandTeam,
	initialSeason,
	Rider,
	Season,
	Series,
	Sport,
	TeamHistory,
} from "../../../../types/model";
import { getBrandTeams } from "../../../../services/brandTeamService";
import { getCurrentSeason } from "../../../../services/seasonService";

interface CreateRiderFormProps {
	riderData: Rider | null;
	setSelectedRider: (rider: Rider) => void;
	fetchRiders: () => void;
}

const CreateRiderForm = ({
	riderData,
	setSelectedRider,
	fetchRiders,
}: CreateRiderFormProps) => {
	const [sportsList, setSportsList] = useState<Sport[]>([]);
	const [seriesList, setSeriesList] = useState<Series[]>([]);
	const [showDeleteRider, setShowDeleteRider] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [brandTeams, setBrandTeams] = useState<BrandTeam[]>([]);
	const [currentSeason, setCurrentSeason] = useState<Season>(initialSeason);

	const [formData, setFormData] = useState<Rider>(
		riderData || {
			first_name: "",
			last_name: "",
			country: "",
			href: "",
			DOB: "",
			team: "",
			team_short: "",
			teamHistory: [],
			gender: "",
			status: "",
			ratings: [],
			sports: [],
			series: [],
			imageUrl: "",
			featured: false,
		},
	);

	useEffect(() => {
		fetchSports();
		fetchSeries();
		fetchBrandTeams();
		fetchCurrentSeason();
	}, []);

	useEffect(() => {
		if (riderData) {
			if (!riderData.status) riderData.status = "";
			setFormData(riderData);
		} else
			setFormData({
				first_name: "",
				last_name: "",
				country: "",
				href: "",
				DOB: "",
				team: "",
				team_short: "",
				gender: "",
				ratings: [],
				sports: [],
				series: [],
				imageUrl: "",
				featured: false,
			});
	}, [riderData]);

	const clearRider = () => {
		setSelectedRider({
			first_name: "",
			last_name: "",
			country: "",
			href: "",
			DOB: "",
			team: "",
			team_short: "",
			gender: "",
			status: "",
			ratings: [],
			sports: [],
			series: [],
			imageUrl: "",
			featured: false,
		});
	};

	const fetchSports = async () => {
		try {
			const res = await getSports();
			if (res) {
				setSportsList(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const fetchSeries = async () => {
		try {
			const res = await getSeries();
			if (res) {
				setSeriesList(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const fetchCurrentSeason = async () => {
		try {
			const res = await getCurrentSeason();
			if (res) {
				setCurrentSeason(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const fetchBrandTeams = async () => {
		try {
			const res = await getBrandTeams();
			if (res) {
				//Sort in alphabetical order
				res.data.sort((a: BrandTeam, b: BrandTeam) =>
					a.name.localeCompare(b.name),
				);
				//Set brand teams
				setBrandTeams(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleSubmit = async (e: any) => {
		e.preventDefault();

		try {
			if (riderData?._id) {
				const res = await updateRider(riderData._id, formData);
				if (res) {
					fetchRiders();
					clearRider();
				}
			} else {
				const res = await createRider(formData);
				if (res) {
					fetchRiders();
					clearRider();
				}
			}
		} catch (error) {
			alert("Error creating rider:" + error);
		}
	};

	const handlUploadPhotoID = async () => {
		if (!selectedFile) return;

		try {
			const formData = new FormData();
			formData.append("image", selectedFile);

			const res = await uploadRiderImage(riderData?._id || "", formData);
			if (res) {
				fetchRiders();
				clearRider();
				setSelectedFile(null);
			}
		} catch (error) {
			alert("Error uploading photo:" + error);
		} finally {
			setSelectedFile(null);
		}
	};

	const handleChange = (e: any) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleTeamHistoryChange = (e: any) => {
		const { value } = e.target;
		const selectedTeam = brandTeams.find((team) => team._id === value);
		if (selectedTeam && selectedTeam._id && currentSeason?._id) {
			setFormData({
				...formData,
				teamHistory: [
					...(formData.teamHistory || []).filter(
						(team: TeamHistory) => team.season !== currentSeason._id,
					),
					{
						season: currentSeason._id,
						team: selectedTeam._id,
					},
				],
			});
		}
	};

	const handleSportAndSeriesChange = (e: any) => {
		const { name, value, options } = e.target;

		const selectedValues = Array.from(options)
			.filter((option: any) => option.selected)
			.map((option: any) => option.value);
		setFormData({
			...formData,
			[name]: selectedValues,
		});
	};

	const handleRatingChange = (e: any) => {
		const { name, value } = e.target;
		const sportId = name.split("-")[1];

		setFormData({
			...formData,
			ratings: [
				...(formData.ratings || []).filter(
					(rating) => rating.sport !== sportId,
				),
				{
					sport: sportId,
					rating: value,
				},
			],
		});
	};

	console.log(
		"FORM DATA",
		currentSeason,
		formData?.teamHistory?.filter(
			(team: TeamHistory) => team.season === currentSeason?._id,
		)[0]?.team,
	);

	const handleDeleteRider = async () => {
		try {
			if (!riderData) return;
			if (!riderData._id) return;

			const res = await deleteRider(riderData._id);
			if (res) {
				fetchRiders();
				setShowDeleteRider(false);
				clearRider();
			}
		} catch (error) {
			alert("Error deleting rider:" + error);
		}
	};

	return (
		<>
			<Card className="p-4 shadow border-0 mt-4" style={{ fontSize: "0.8rem" }}>
				<Card.Title>Create a new rider</Card.Title>
				<Card.Body>
					<Form onSubmit={handleSubmit}>
						<Row>
							<Col sm={4}>
								<Button variant="outline-secondary" onClick={clearRider}>
									Clear Selected Rider
								</Button>
							</Col>
						</Row>
						<Row className="mt-3">
							<Col sm={3}>
								<Form.Group controlId="first_name">
									<Form.Label>First Name</Form.Label>
									<Form.Control
										type="text"
										name="first_name"
										value={formData.first_name}
										onChange={handleChange}
									/>
								</Form.Group>
							</Col>
							<Col sm={3}>
								<Form.Group controlId="last_name">
									<Form.Label>Last Name</Form.Label>
									<Form.Control
										type="text"
										name="last_name"
										value={formData.last_name}
										onChange={handleChange}
									/>
								</Form.Group>
							</Col>
							<Col sm={3}>
								<Form.Group controlId="country">
									<Form.Label>Country</Form.Label>
									<Form.Control
										type="text"
										name="country"
										value={formData.country}
										onChange={handleChange}
									/>
								</Form.Group>
							</Col>
							<Col sm={3}>
								<Form.Group controlId="gender">
									<Form.Label>Gender</Form.Label>
									<Form.Control
										as="select"
										name="gender"
										value={formData.gender}
										onChange={handleChange}
									>
										<option style={{ fontSize: "0.8rem" }} value=""></option>
										<option style={{ fontSize: "0.8rem" }} value="m">
											Male
										</option>
										<option style={{ fontSize: "0.8rem" }} value="f">
											Female
										</option>
									</Form.Control>
								</Form.Group>
							</Col>
						</Row>
						<Row className="mt-3">
							<Col sm={3}>
								<Form.Group controlId="DOB">
									<Form.Label>Date of Birth</Form.Label>
									<Form.Control
										type="date"
										name="DOB"
										value={formData.DOB}
										onChange={handleChange}
									/>
								</Form.Group>
							</Col>
							<Col sm={3}>
								<Form.Group controlId="teamHistory">
									<Form.Label>Team History</Form.Label>
									<Form.Control
										as="select"
										name="teamHistory"
										value={
											formData?.teamHistory?.filter(
												(team: TeamHistory) =>
													team.season === currentSeason?._id,
											)[0]?.team
										}
										onChange={handleTeamHistoryChange}
									>
										{brandTeams.map((team: BrandTeam) => (
											<option
												style={{ fontSize: "0.8rem" }}
												key={team._id}
												value={team._id}
											>
												{team.name}
											</option>
										))}
									</Form.Control>
								</Form.Group>
								{/* <Form.Group controlId="team">
									<Form.Label>Team</Form.Label>
									<Form.Control
										type="text"
										name="team"
										value={formData.team}
										onChange={handleChange}
									/>
								</Form.Group> */}
							</Col>
							<Col sm={3}>
								<Form.Group controlId="team_short">
									<Form.Label>Team Short</Form.Label>
									<Form.Control
										type="text"
										name="team_short"
										value={formData.team_short}
										onChange={handleChange}
									/>
								</Form.Group>
							</Col>
							<Col sm={3}>
								<Form.Group controlId="status">
									<Form.Label>Status</Form.Label>
									<Form.Control
										as="select"
										name="status"
										value={formData.status}
										onChange={handleChange}
									>
										<option style={{ fontSize: "0.8rem" }} value="Active">
											Active
										</option>
										<option style={{ fontSize: "0.8rem" }} value="Injured">
											Injured
										</option>
										<option style={{ fontSize: "0.8rem" }} value="Ineligible">
											Ineligible
										</option>
									</Form.Control>
								</Form.Group>
							</Col>
						</Row>
						<Row className="mt-3">
							<Col sm={6}>
								<Form.Group controlId="sports">
									<Form.Label>Select Sports</Form.Label>
									<Form.Control
										as="select"
										multiple
										name="sports"
										value={formData.sports}
										onChange={handleSportAndSeriesChange}
									>
										{sportsList.map((sport: Sport) => (
											<option
												style={{ fontSize: "0.8rem" }}
												key={sport?._id}
												value={sport?._id}
											>
												{sport.name}
											</option>
										))}
									</Form.Control>
								</Form.Group>
							</Col>
							<Col sm={6}>
								<Form.Group controlId="series">
									<Form.Label>Select Series</Form.Label>
									<Form.Control
										as="select"
										multiple
										name="series"
										value={formData.series}
										onChange={handleSportAndSeriesChange}
									>
										{seriesList.map((series: Series) => (
											<option
												style={{ fontSize: "0.8rem" }}
												key={series._id}
												value={series._id}
											>
												{series.name}
											</option>
										))}
									</Form.Control>
								</Form.Group>
							</Col>
						</Row>
						{/* Ratings for each selected sport */}
						{sportsList.map(
							(sport: Sport) =>
								(formData.sports ?? []).includes(sport?._id ?? "") && (
									<Row key={sport._id} className="mt-3">
										<Col>
											<Form.Group controlId={`ratings-${sport._id}`}>
												<Form.Label>
													Ratings for {sport.name} (Out of 100)
												</Form.Label>
												<Form.Control
													type="number"
													name={`ratings-${sport._id}`}
													value={
														(formData.ratings ?? []).find(
															(rating) => rating.sport === sport._id,
														)?.rating || ""
													}
													onChange={handleRatingChange}
												/>
											</Form.Group>
										</Col>
									</Row>
								),
						)}

						{/* Featured */}
						<Row className="mt-3">
							<Col>
								<Form.Group controlId="featured">
									<Form.Check
										type="checkbox"
										label="Featured"
										name="featured"
										checked={formData.featured}
										onChange={(e) =>
											setFormData({
												...formData,
												featured: e.target.checked,
											})
										}
									/>
								</Form.Group>
							</Col>
						</Row>
						{riderData && riderData.imageUrl && (
							<Row className="mt-3">
								<Col>
									<Form.Group controlId="imagePreview">
										<Form.Label>Image Preview</Form.Label>
										<div>
											<img
												src={riderData.imageUrl}
												alt="Rider"
												style={{ maxWidth: "200px", maxHeight: "auto" }}
											/>
										</div>
									</Form.Group>
								</Col>
							</Row>
						)}
						<Row className="mt-3">
							<Col sm={8}>
								<Form.Group controlId="photo">
									<Form.Label>Upload Photo</Form.Label>
									<Form.Control
										type="file"
										accept="image/png, image/jpeg, image/jpg"
										onChange={(e: any) => {
											if (e.target.files && e.target.files[0]) {
												const file = e.target.files[0];
												setSelectedFile(file);
											}
										}}
									/>
								</Form.Group>
							</Col>
							<Col sm={4} className="d-flex align-items-end">
								<Button variant="secondary" onClick={handlUploadPhotoID}>
									Upload
								</Button>
							</Col>
						</Row>
						<Row>
							<Col>
								<Button variant="primary" type="submit" className="mt-3">
									{riderData?._id ? "Update Rider" : "Create Rider"}
								</Button>
							</Col>
							<Col>
								<Button
									variant="dark"
									onClick={() => setShowDeleteRider(true)}
									className="mt-3"
								>
									Delete Rider
								</Button>
							</Col>
						</Row>
					</Form>
				</Card.Body>
			</Card>
			<Modal show={showDeleteRider} onHide={() => setShowDeleteRider(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Delete Rider</Modal.Title>
				</Modal.Header>
				<Modal.Body>Are you sure you want to delete this rider?</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowDeleteRider(false)}>
						Close
					</Button>
					<Button variant="danger" onClick={handleDeleteRider}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default CreateRiderForm;
